import styled, { keyframes } from "styled-components";
import { COLORS } from "../../common/colors";

// ─── Design tokens — pulled from centralized colors.js ────────────────────────
export const C = {
  brand:      COLORS.primary,        // Sage Forest  #3d6b5e
  brandLight: COLORS.primaryLight,   // Eucalyptus   #7ab5a0
  brandDark:  COLORS.primaryDark,    // Deep Forest  #2b4a42
  accent:     COLORS.accent,         // Warm Sand    #c9a96e
  surface:    COLORS.white,          // Parchment    #fdfaf6
  surfaceAlt: COLORS.bgCool,         // Sage Mist    #e8f0ee
  border:     COLORS.border,         // green-tinted border
  text:       COLORS.textDark,       // Deep Forest  #2b4a42
  textMid:    COLORS.textBody,       // Bark Brown   #4a3728
  textSoft:   COLORS.textMid,        // Stone Sage   #8fa89e
  muted:      COLORS.bgLight,        // Pale Jade    #d4e8e1
  bg:         COLORS.pageBg,         // Parchment    #fdfaf6
};

// px breakpoints
// BP_NAV needs to be wide enough that the logo, all 9 links, and the CTA
// can lay out at a comfortable reading size without crowding each other —
// 860px was too narrow for that many links and caused them to visually
// collide with the logo/CTA on real desktop widths just above it.
const BP_NAV = "1300px";
const BP_MOB = "480px";

// ─── Keyframes ────────────────────────────────────────────────────────────────
const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-100%); }
  to   { opacity: 1; transform: translateY(0); }
`;

const linkSlide = keyframes`
  from { opacity: 0; transform: translateX(18px); }
  to   { opacity: 1; transform: translateX(0); }
`;

// ─── Nav shell ────────────────────────────────────────────────────────────────
export const NavSection = styled.header`
  position: sticky;
  top: 0;
  z-index: 200;
  background: ${COLORS.white};
  border-bottom: 1px solid ${COLORS.border};
  box-shadow: 0 1px 12px ${COLORS.primaryShadow};
  animation: ${slideDown} 0.4s ease both;

  /* Accent gradient bar on scroll */
  &::after {
    content: "";
    display: block;
    height: 2.5px;
    background: linear-gradient(90deg, ${COLORS.primaryDark}, ${COLORS.primary}, ${COLORS.accent});
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    opacity: 0;
    transition: opacity 0.3s;
  }
  &[data-scrolled="true"]::after { opacity: 1; }
`;

// ─── Inner bar ────────────────────────────────────────────────────────────────
export const NavBar = styled.nav`
  display: flex;
  align-items: center;
  gap: 20px;
  max-width: 1320px;
  height: 68px;
  margin: 0 auto;
  padding: 0 32px;

  @media (max-width: ${BP_NAV}) {
    padding: 0 16px;
    height: 60px;
  }
`;

// ─── Logo ─────────────────────────────────────────────────────────────────────
// ─── Logo ─────────────────────────────────────────────────────────────────────
export const LogoArea = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  cursor: pointer;

  /* Prevent the logo from being squeezed */
  width: 210px;
  height: 60px;

  @media (max-width: ${BP_NAV}) {
    width: 170px;
    height: 50px;
  }

  @media (max-width: ${BP_MOB}) {
    width: 145px;
    height: 45px;
  }
`;

// ─── Desktop menu ─────────────────────────────────────────────────────────────
// A real flex child (flex: 1) that centers its own content, rather than an
// absolutely-positioned overlay centered on the whole bar. The old approach
// ignored how wide the logo and CTA actually were, so on real desktop widths
// the 9 links just sat on top of them instead of being pushed clear.
export const DesktopMenu = styled.ul`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  min-width: 0;
  list-style: none;
  gap: 2px;
  margin: 0;
  padding: 0;

  /* Safety net: if content ever needs more room than the bar has (browser
     zoom, font-size overrides, a future extra link) it scrolls instead of
     overlapping the logo or CTA. Scrollbar hidden since this is a nav, not
     a scroll area users should notice. */
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }

  @media (max-width: ${BP_NAV}) { display: none; }
`;

export const NavLink = styled.button`
  background: none;
  border: none;
  padding: 7px 8px;
  font-size: 0.82rem;
  font-weight: 600;
  color: ${({ $active }) => ($active ? COLORS.primary : COLORS.textBody)};
  cursor: pointer;
  border-radius: 10px;
  transition: color 0.2s, background 0.2s;
  white-space: nowrap;
  position: relative;
  letter-spacing: normal;

  &::before {
    content: "";
    position: absolute;
    bottom: 2px;
    left: 50%;
    transform: translateX(-50%) scaleX(${({ $active }) => ($active ? 1 : 0)});
    width: 16px;
    height: 2px;
    background: linear-gradient(90deg, ${COLORS.primary}, ${COLORS.accent});
    border-radius: 2px;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  &:hover {
    color: ${COLORS.primary};
    background: ${COLORS.bgLight};
    &::before { transform: translateX(-50%) scaleX(1); }
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.primary};
    outline-offset: 2px;
  }
`;

// ─── Desktop CTA ──────────────────────────────────────────────────────────────
export const NavCta = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px;
  background: linear-gradient(135deg, ${COLORS.primaryDark}, ${COLORS.primary});
  color: #fff;
  border: none;
  border-radius: 22px;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 4px 16px ${COLORS.primaryGlow};

  svg { width: 14px; height: 14px; transition: transform 0.2s; }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px ${COLORS.primaryGlow};
    svg { transform: translateX(2px); }
  }
  &:active { transform: translateY(0); }
  &:focus-visible { outline: 2px solid ${COLORS.accent}; outline-offset: 3px; }

  @media (max-width: ${BP_NAV}) { display: none; }
`;

// ─── Hamburger button ─────────────────────────────────────────────────────────
export const HamBtn = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  background: ${COLORS.bgCool};
  border: 1.5px solid ${COLORS.border};
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
  flex-shrink: 0;
  padding: 0;
  margin-left: auto;

  @media (max-width: ${BP_NAV}) { display: flex; }

  &:hover {
    background: ${COLORS.bgLight};
    border-color: ${COLORS.borderGreen20};
  }
  &:focus-visible { outline: 2px solid ${COLORS.primary}; outline-offset: 2px; }
`;

export const HamIcon = styled.span`
  position: relative;
  display: block;
  width: 20px;
  height: 2px;
  background: ${({ $open }) => ($open ? "transparent" : COLORS.textDark)};
  border-radius: 2px;
  transition: background 0.25s;

  &::before,
  &::after {
    content: "";
    position: absolute;
    left: 0;
    width: 100%;
    height: 2px;
    background: ${COLORS.textDark};
    border-radius: 2px;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), top 0.25s, bottom 0.25s;
  }
  &::before {
    top: ${({ $open }) => ($open ? "0" : "-6px")};
    transform: ${({ $open }) => ($open ? "rotate(45deg)" : "none")};
  }
  &::after {
    bottom: ${({ $open }) => ($open ? "0" : "-6px")};
    transform: ${({ $open }) => ($open ? "rotate(-45deg)" : "none")};
  }
`;

// ─── Mobile overlay ───────────────────────────────────────────────────────────
export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 198;
  background: ${COLORS.overlay};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  transition: opacity 0.3s ease;

  @media (min-width: ${BP_NAV}) { display: none; }
`;

// ─── Mobile drawer ────────────────────────────────────────────────────────────
export const Drawer = styled.div`
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 199;
  width: min(320px, 88vw);
  background: ${COLORS.pageBg};
  box-shadow: -8px 0 48px ${COLORS.primaryShadow};
  transform: ${({ $open }) => ($open ? "translateX(0)" : "translateX(110%)")};
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-y: auto;
  overflow-x: hidden;

  @media (min-width: ${BP_NAV}) { display: none; }
`;

export const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${COLORS.border};
  flex-shrink: 0;
`;

export const DrawerClose = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: ${COLORS.bgLight};
  border: none;
  border-radius: 10px;
  cursor: pointer;
  color: ${COLORS.primary};
  font-size: 1.1rem;
  font-weight: 700;
  transition: background 0.2s;

  &:hover { background: ${COLORS.bgCool}; }
  &:focus-visible { outline: 2px solid ${COLORS.primary}; outline-offset: 2px; }
`;

export const DrawerLinks = styled.ul`
  list-style: none;
  padding: 16px 12px;
  margin: 0;
  flex: 1;
`;

export const DrawerLinkItem = styled.li`
  animation: ${linkSlide} 0.32s ease both;
  animation-delay: ${({ $i }) => 0.05 + ($i || 0) * 0.045}s;
`;

export const DrawerLink = styled.button`
  width: 100%;
  text-align: left;
  background: ${({ $active }) => ($active ? COLORS.bgLight : "none")};
  border: none;
  padding: 13px 16px;
  font-size: 1rem;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  color: ${({ $active }) => ($active ? COLORS.primary : COLORS.textBody)};
  cursor: pointer;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background 0.18s, color 0.18s;

  &:hover { background: ${COLORS.bgLight}; color: ${COLORS.primary}; }
  &:focus-visible { outline: 2px solid ${COLORS.primary}; outline-offset: 2px; }
`;

export const DrawerLinkArrow = styled.span`
  font-size: 0.8rem;
  opacity: 0.5;
`;

export const DrawerDivider = styled.div`
  height: 1px;
  background: ${COLORS.border};
  margin: 8px 12px;
`;

export const DrawerCta = styled.button`
  margin: 8px 12px 24px;
  width: calc(100% - 24px);
  padding: 14px 20px;
  background: linear-gradient(135deg, ${COLORS.primaryDark}, ${COLORS.primary});
  color: #fff;
  border: none;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  box-shadow: 0 4px 20px ${COLORS.primaryGlow};
  transition: transform 0.2s, box-shadow 0.2s;
  animation: ${linkSlide} 0.32s ease both;
  animation-delay: 0.4s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px ${COLORS.primaryGlow};
  }
  &:active { transform: translateY(0); }
  &:focus-visible { outline: 2px solid ${COLORS.accent}; outline-offset: 3px; }
`;